import type { TFunction } from "i18next";

import type {
  SprengnetterValuationHubProsaWebModelResponseProsaWebEditableFactorWithConfidenceIntervalResponse as ProsaWebDisplayFactorResponse,
  SprengnetterValuationHubProsaWebModelResponseProsaWebMarketFactorWithConfidenceIntervalResponse as ProsaWebFactorResponse,
  SprengnetterValuationHubProsaWebModelResponseProsaWebResponse as ProsaWebResponse,
  ValuationHubCoreModelRealEstateMixedUseFactorsInput,
  ValuationHubCoreModelRealEstateProsaWebFactorsInput,
} from "@/api/generatedApi";
import { ObjectTypeEnum } from "@/types/refineRealEstate";

interface ProsaWebMarketValueOptions {
  rounded?: boolean;
  useRoundedValue?: boolean;
  objectType?: ObjectTypeEnum;
  livingArea?: number | null;
}

interface UpdateProsaWebIndicativeFactorOptions {
  syncMarketValue?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type GenericTranslator = TFunction<any>;

const roundToThousands = (value: number): number => Math.round(value / 1000) * 1000;

const getTranslationOrFallback = (t: GenericTranslator, key: string, fallback = "-"): string => {
  const translated = t(key);
  return translated || fallback;
};

export const getProsaWebMarketValue = (
  prosaWeb?: ProsaWebResponse | null,
  options?: ProsaWebMarketValueOptions,
): number | null => {
  const marketValue = prosaWeb?.market_value;
  const baseValue = marketValue?.rounded_value ?? marketValue?.value ?? null;

  if (baseValue == null) {
    return null;
  }

  if (options?.rounded === false) {
    return baseValue;
  }

  if (options?.useRoundedValue) {
    return roundToThousands(baseValue);
  }

  return roundToThousands(baseValue);
};

const isApartmentObjectType = (objectType?: ObjectTypeEnum): boolean => objectType === ObjectTypeEnum.apartment;
const isMultiFamilyHouseObjectType = (objectType?: ObjectTypeEnum): boolean =>
  objectType === ObjectTypeEnum.multiFamilyHouse;
const isMixedUseObjectType = (objectType?: ObjectTypeEnum): boolean => objectType === ObjectTypeEnum.mixedUseProperty;
const isPlotObjectType = (objectType?: ObjectTypeEnum): boolean => objectType === ObjectTypeEnum.plotOfLand;

export const getProsaWebIndicativeFactor = (
  prosaWeb?: ProsaWebResponse | null,
  objectType?: ObjectTypeEnum,
): ProsaWebFactorResponse | null | undefined => {
  if (isApartmentObjectType(objectType)) {
    return prosaWeb?.comparable_sales_approach?.comparable_price;
  }

  if (isMultiFamilyHouseObjectType(objectType)) {
    return prosaWeb?.income_approach?.property_yield_rate;
  }

  if (isPlotObjectType(objectType)) {
    return prosaWeb?.comparable_sales_approach?.standard_ground_value;
  }

  return prosaWeb?.cost_approach?.asset_value_factor;
};

export const getProsaWebDisplayFactor = (
  prosaWeb?: ProsaWebResponse | null,
  objectType?: ObjectTypeEnum,
): ProsaWebDisplayFactorResponse | ProsaWebFactorResponse | null | undefined => {
  if (isApartmentObjectType(objectType)) {
    return prosaWeb?.factors?.comparable_price;
  }

  if (isMultiFamilyHouseObjectType(objectType)) {
    return prosaWeb?.income_approach?.property_yield_rate;
  }

  if (isPlotObjectType(objectType)) {
    return prosaWeb?.factors?.standard_ground_value;
  }

  return prosaWeb?.factors?.asset_value_factor;
};

export type ProsaWebFactorsInputWithIncome = ValuationHubCoreModelRealEstateProsaWebFactorsInput & {
  property_yield_rate?: number | null;
};

export const getProsaWebFactorInput = (
  objectType: ObjectTypeEnum | undefined,
  factorValue: number,
): ProsaWebFactorsInputWithIncome => {
  if (isApartmentObjectType(objectType)) {
    return { comparable_price: factorValue };
  }

  if (isMultiFamilyHouseObjectType(objectType)) {
    return { property_yield_rate: factorValue };
  }

  if (isPlotObjectType(objectType)) {
    return { standard_ground_value: factorValue };
  }

  return { asset_value_factor: factorValue };
};

export const getProsaWebFactorsInput = (
  prosaWeb?: ProsaWebResponse | null,
  objectType?: ObjectTypeEnum,
): ProsaWebFactorsInputWithIncome | null => {
  if (isMixedUseObjectType(objectType)) {
    const propertyYieldRate = prosaWeb?.factors?.property_yield_rate;
    const comparableRent = prosaWeb?.factors?.comparable_rent;
    const mixedUseFactors: ValuationHubCoreModelRealEstateMixedUseFactorsInput = {
      property_yield_rate: {
        residential:
          propertyYieldRate?.residential?.value != null ? { value: propertyYieldRate.residential.value } : undefined,
        commercial:
          propertyYieldRate?.commercial?.value != null ? { value: propertyYieldRate.commercial.value } : undefined,
      },
      comparable_rent: {
        residential:
          comparableRent?.residential?.value != null ? { value: comparableRent.residential.value } : undefined,
        commercial: comparableRent?.commercial?.value != null ? { value: comparableRent.commercial.value } : undefined,
      },
    };
    const hasMixedUseFactor =
      mixedUseFactors.property_yield_rate?.residential ||
      mixedUseFactors.property_yield_rate?.commercial ||
      mixedUseFactors.comparable_rent?.residential ||
      mixedUseFactors.comparable_rent?.commercial;

    return hasMixedUseFactor ? { mixed_use_factors: mixedUseFactors } : null;
  }

  const factorValue = getProsaWebDisplayFactor(prosaWeb, objectType)?.value;

  if (typeof factorValue !== "number" || !Number.isFinite(factorValue)) {
    return null;
  }

  if (isMultiFamilyHouseObjectType(objectType)) {
    const comparableRentValue = prosaWeb?.income_approach?.comparable_rent?.value;

    return {
      property_yield_rate: factorValue,
      ...(typeof comparableRentValue === "number" && Number.isFinite(comparableRentValue)
        ? { comparable_rent: comparableRentValue }
        : {}),
    };
  }

  return getProsaWebFactorInput(objectType, factorValue);
};

export const getProsaWebIndicativePreliminaryValue = (
  prosaWeb?: ProsaWebResponse | null,
  objectType?: ObjectTypeEnum,
): number | null | undefined => {
  if (isApartmentObjectType(objectType)) {
    return prosaWeb?.comparable_sales_approach?.preliminary_value ?? prosaWeb?.comparable_sales_approach?.value;
  }

  if (isMultiFamilyHouseObjectType(objectType)) {
    return prosaWeb?.income_approach?.preliminary_value ?? prosaWeb?.income_approach?.value;
  }

  return prosaWeb?.cost_approach?.preliminary_value ?? prosaWeb?.cost_approach?.value;
};

export const getProsaWebIndicativeAdjustedValue = (
  prosaWeb?: ProsaWebResponse | null,
  objectType?: ObjectTypeEnum,
): number | null | undefined => {
  if (isApartmentObjectType(objectType)) {
    return prosaWeb?.comparable_sales_approach?.value;
  }

  if (isMultiFamilyHouseObjectType(objectType)) {
    return prosaWeb?.income_approach?.value;
  }

  return prosaWeb?.cost_approach?.value;
};

const updateApartmentProsaWebIndicativeFactor = (
  prosaWeb: ProsaWebResponse,
  factorValue: number,
  livingArea?: number | null,
  options?: UpdateProsaWebIndicativeFactorOptions,
): ProsaWebResponse => {
  const updatedComparisonValue =
    typeof livingArea === "number" && Number.isFinite(livingArea)
      ? factorValue * livingArea
      : prosaWeb.comparable_sales_approach?.value ?? null;

  return {
    ...prosaWeb,
    comparable_sales_approach: {
      ...prosaWeb.comparable_sales_approach,
      value: updatedComparisonValue,
      comparable_price: {
        ...prosaWeb.comparable_sales_approach?.comparable_price,
        value: factorValue,
      },
    },
    factors: {
      ...prosaWeb.factors,
      comparable_price: {
        ...prosaWeb.comparable_sales_approach?.comparable_price,
        ...prosaWeb.factors?.comparable_price,
        value: factorValue,
      },
    },
    ...(options?.syncMarketValue
      ? {
          market_value: {
            ...prosaWeb.market_value,
            value: updatedComparisonValue,
            rounded_value:
              typeof updatedComparisonValue === "number" && Number.isFinite(updatedComparisonValue)
                ? roundToThousands(updatedComparisonValue)
                : prosaWeb.market_value?.rounded_value ?? null,
          },
        }
      : {}),
  };
};

const updateHouseProsaWebIndicativeFactor = (
  prosaWeb: ProsaWebResponse,
  factorValue: number,
  options?: UpdateProsaWebIndicativeFactorOptions,
): ProsaWebResponse => {
  const preliminaryValue = prosaWeb.cost_approach?.preliminary_value ?? prosaWeb.cost_approach?.value;
  const updatedMarketValue =
    preliminaryValue != null ? preliminaryValue * factorValue : prosaWeb.cost_approach?.value ?? null;

  return {
    ...prosaWeb,
    cost_approach: {
      ...prosaWeb.cost_approach,
      value: updatedMarketValue,
      asset_value_factor: {
        ...prosaWeb.cost_approach?.asset_value_factor,
        value: factorValue,
      },
    },
    factors: {
      ...prosaWeb.factors,
      asset_value_factor: {
        ...prosaWeb.cost_approach?.asset_value_factor,
        ...prosaWeb.factors?.asset_value_factor,
        value: factorValue,
      },
    },
    ...(options?.syncMarketValue
      ? {
          market_value: {
            ...prosaWeb.market_value,
            value: updatedMarketValue,
            rounded_value:
              typeof updatedMarketValue === "number" && Number.isFinite(updatedMarketValue)
                ? roundToThousands(updatedMarketValue)
                : prosaWeb.market_value?.rounded_value ?? null,
          },
        }
      : {}),
  };
};

const updatePlotProsaWebIndicativeFactor = (
  prosaWeb: ProsaWebResponse,
  factorValue: number,
  plotArea?: number | null,
  options?: UpdateProsaWebIndicativeFactorOptions,
): ProsaWebResponse => {
  const updatedLandValue =
    typeof plotArea === "number" && Number.isFinite(plotArea)
      ? factorValue * plotArea
      : prosaWeb.market_value?.value ?? null;

  return {
    ...prosaWeb,
    factors: {
      ...prosaWeb.factors,
      standard_ground_value: {
        ...prosaWeb.factors?.standard_ground_value,
        value: factorValue,
      },
    },
    ...(options?.syncMarketValue
      ? {
          market_value: {
            ...prosaWeb.market_value,
            value: updatedLandValue,
            rounded_value:
              typeof updatedLandValue === "number" && Number.isFinite(updatedLandValue)
                ? roundToThousands(updatedLandValue)
                : prosaWeb.market_value?.rounded_value ?? null,
          },
        }
      : {}),
  };
};

const updateIncomeProsaWebIndicativeFactors = (
  prosaWeb: ProsaWebResponse,
  factors: ValuationHubCoreModelRealEstateProsaWebFactorsInput,
  options?: UpdateProsaWebIndicativeFactorOptions,
): ProsaWebResponse => {
  const factorValue = factors.property_yield_rate;
  const hasFactorValue = typeof factorValue === "number" && Number.isFinite(factorValue);
  const preliminaryValue = prosaWeb.income_approach?.preliminary_value ?? prosaWeb.income_approach?.value;
  const updatedIncomeValue =
    hasFactorValue && preliminaryValue != null
      ? preliminaryValue * factorValue
      : prosaWeb.income_approach?.value ?? null;

  return {
    ...prosaWeb,
    income_approach: {
      ...prosaWeb.income_approach,
      value: updatedIncomeValue,
      property_yield_rate: {
        ...prosaWeb.income_approach?.property_yield_rate,
        ...(hasFactorValue ? { value: factorValue } : {}),
      },
      comparable_rent: {
        ...prosaWeb.income_approach?.comparable_rent,
        ...(typeof factors.comparable_rent === "number" && Number.isFinite(factors.comparable_rent)
          ? { value: factors.comparable_rent }
          : {}),
      },
    },
    ...(options?.syncMarketValue
      ? {
          market_value: {
            ...prosaWeb.market_value,
            value: updatedIncomeValue,
            rounded_value:
              typeof updatedIncomeValue === "number" && Number.isFinite(updatedIncomeValue)
                ? roundToThousands(updatedIncomeValue)
                : prosaWeb.market_value?.rounded_value ?? null,
          },
        }
      : {}),
  };
};

const updateIncomeProsaWebIndicativeFactor = (
  prosaWeb: ProsaWebResponse,
  factorValue: number,
  options?: UpdateProsaWebIndicativeFactorOptions,
): ProsaWebResponse => updateIncomeProsaWebIndicativeFactors(prosaWeb, { property_yield_rate: factorValue }, options);

export const updateProsaWebIndicativeFactor = (
  prosaWeb: ProsaWebResponse | null | undefined,
  objectType: ObjectTypeEnum | undefined,
  factorValue: number,
  livingArea?: number | null,
  options?: UpdateProsaWebIndicativeFactorOptions,
): ProsaWebResponse | null => {
  if (!prosaWeb) {
    return null;
  }

  if (isApartmentObjectType(objectType)) {
    return updateApartmentProsaWebIndicativeFactor(prosaWeb, factorValue, livingArea, options);
  }

  if (isMultiFamilyHouseObjectType(objectType)) {
    return updateIncomeProsaWebIndicativeFactor(prosaWeb, factorValue, options);
  }

  if (isPlotObjectType(objectType)) {
    return updatePlotProsaWebIndicativeFactor(prosaWeb, factorValue, livingArea, options);
  }

  return updateHouseProsaWebIndicativeFactor(prosaWeb, factorValue, options);
};

const updateMixedUseProsaWebIndicativeFactors = (
  prosaWeb: ProsaWebResponse,
  factors: ValuationHubCoreModelRealEstateMixedUseFactorsInput,
): ProsaWebResponse => {
  const propertyYieldRate = factors.property_yield_rate;
  const comparableRent = factors.comparable_rent;

  return {
    ...prosaWeb,
    factors: {
      ...prosaWeb.factors,
      property_yield_rate: {
        ...prosaWeb.factors?.property_yield_rate,
        residential: {
          ...prosaWeb.factors?.property_yield_rate?.residential,
          ...(propertyYieldRate?.residential?.value != null ? { value: propertyYieldRate.residential.value } : {}),
        },
        commercial: {
          ...prosaWeb.factors?.property_yield_rate?.commercial,
          ...(propertyYieldRate?.commercial?.value != null ? { value: propertyYieldRate.commercial.value } : {}),
        },
      },
      comparable_rent: {
        ...prosaWeb.factors?.comparable_rent,
        residential: {
          ...prosaWeb.factors?.comparable_rent?.residential,
          ...(comparableRent?.residential?.value != null ? { value: comparableRent.residential.value } : {}),
        },
        commercial: {
          ...prosaWeb.factors?.comparable_rent?.commercial,
          ...(comparableRent?.commercial?.value != null ? { value: comparableRent.commercial.value } : {}),
        },
      },
    },
  };
};

export const updateProsaWebIndicativeFactors = (
  prosaWeb: ProsaWebResponse | null | undefined,
  objectType: ObjectTypeEnum | undefined,
  factors: ValuationHubCoreModelRealEstateProsaWebFactorsInput,
  livingArea?: number | null,
  options?: UpdateProsaWebIndicativeFactorOptions,
): ProsaWebResponse | null => {
  if (!prosaWeb) {
    return null;
  }

  if (isMixedUseObjectType(objectType)) {
    return factors.mixed_use_factors
      ? updateMixedUseProsaWebIndicativeFactors(prosaWeb, factors.mixed_use_factors)
      : prosaWeb;
  }

  if (isApartmentObjectType(objectType)) {
    return typeof factors.comparable_price === "number"
      ? updateApartmentProsaWebIndicativeFactor(prosaWeb, factors.comparable_price, livingArea, options)
      : prosaWeb;
  }

  if (isMultiFamilyHouseObjectType(objectType)) {
    return updateIncomeProsaWebIndicativeFactors(prosaWeb, factors, options);
  }

  if (isPlotObjectType(objectType)) {
    return typeof factors.standard_ground_value === "number"
      ? updatePlotProsaWebIndicativeFactor(prosaWeb, factors.standard_ground_value, livingArea, options)
      : prosaWeb;
  }

  return typeof factors.asset_value_factor === "number"
    ? updateHouseProsaWebIndicativeFactor(prosaWeb, factors.asset_value_factor, options)
    : prosaWeb;
};

export const applySavedProsaWebFactors = (
  prosaWeb: ProsaWebResponse | null | undefined,
  objectType: ObjectTypeEnum | undefined,
  factors: ValuationHubCoreModelRealEstateProsaWebFactorsInput | null | undefined,
  livingArea?: number | null,
): ProsaWebResponse | null => {
  if (!prosaWeb) {
    return null;
  }

  if (isMixedUseObjectType(objectType)) {
    return factors?.mixed_use_factors
      ? updateMixedUseProsaWebIndicativeFactors(prosaWeb, factors.mixed_use_factors)
      : prosaWeb;
  }

  if (isApartmentObjectType(objectType)) {
    return typeof factors?.comparable_price === "number"
      ? updateApartmentProsaWebIndicativeFactor(prosaWeb, factors.comparable_price, livingArea)
      : prosaWeb;
  }

  if (isMultiFamilyHouseObjectType(objectType)) {
    return factors ? updateIncomeProsaWebIndicativeFactors(prosaWeb, factors) : prosaWeb;
  }

  if (isPlotObjectType(objectType)) {
    return typeof factors?.standard_ground_value === "number"
      ? updatePlotProsaWebIndicativeFactor(prosaWeb, factors.standard_ground_value, livingArea)
      : prosaWeb;
  }

  return typeof factors?.asset_value_factor === "number"
    ? updateHouseProsaWebIndicativeFactor(prosaWeb, factors.asset_value_factor)
    : prosaWeb;
};

export const getProsaWebBuildingStandardLabel = (value: number | null | undefined, t: GenericTranslator): string => {
  if (value == null || !Number.isFinite(value)) {
    return "-";
  }

  if (value <= 1.25) return getTranslationOrFallback(t, "equipmentValueLabel.verySimple");
  if (value <= 1.75) return getTranslationOrFallback(t, "equipmentValueLabel.simple");
  if (value <= 2.25) return getTranslationOrFallback(t, "equipmentValueLabel.simpleToMedium");
  if (value <= 2.75) return getTranslationOrFallback(t, "equipmentValueLabel.medium");
  if (value <= 3.25) return getTranslationOrFallback(t, "equipmentValueLabel.mediumToHigh");
  if (value <= 3.75) return getTranslationOrFallback(t, "equipmentValueLabel.high");
  if (value <= 4.25) return getTranslationOrFallback(t, "equipmentValueLabel.highToVeryHigh");
  if (value <= 4.75) return getTranslationOrFallback(t, "equipmentValueLabel.veryHigh");

  return getTranslationOrFallback(t, "equipmentValueLabel.veryVeryHigh");
};

export const getProsaWebModernizationLabel = (value: number | null | undefined, t: GenericTranslator): string => {
  if (value == null || !Number.isFinite(value)) {
    return "-";
  }

  if (value <= 1) return getTranslationOrFallback(t, "modernizationClass.short.none");
  if (value <= 5) return getTranslationOrFallback(t, "modernizationClass.short.light");
  if (value <= 10) return getTranslationOrFallback(t, "modernizationClass.short.medium");
  if (value <= 17) return getTranslationOrFallback(t, "modernizationClass.short.most");

  return getTranslationOrFallback(t, "modernizationClass.full");
};
